import { useState, useEffect } from 'react'
import { AdminApiService } from '../services/api'
import { DashboardStats } from '../types/admin'
import CassetteLoader from '../components/CassetteLoader'

export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadDashboardStats()
  }, [])

  const loadDashboardStats = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await AdminApiService.getDashboardStats()
      setStats(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard stats')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CassetteLoader size={80} text="Loading dashboard..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          onClick={loadDashboardStats}
          className="btn btn-primary"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-600">No dashboard data available</div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">
          Overview of your Cassette platform
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Users */}
        <div className="card animate-fade-in-delay">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total Users
                </dt>
                <dd className="text-2xl font-bold text-gray-900">
                  {stats.users.totalUsers.toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        {/* Premium Users */}
        <div className="card animate-fade-in-delay-2">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Premium Users
                </dt>
                <dd className="text-2xl font-bold text-gray-900">
                  {stats.users.premiumUsers.toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        {/* New Users Today */}
        <div className="card animate-fade-in-delay-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  New Today
                </dt>
                <dd className="text-2xl font-bold text-gray-900">
                  {stats.users.newUsersToday.toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        {/* Active Users */}
        <div className="card animate-fade-in-delay-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Active Users
                </dt>
                <dd className="text-2xl font-bold text-gray-900">
                  {stats.users.activeUsers.toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Tiers */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">
            Subscription Tiers
          </h3>
          <p className="text-sm text-gray-600">User distribution across subscription levels</p>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <div className="text-3xl font-bold text-gray-900">
                {stats.subscriptions.starterUsers}
              </div>
              <div className="text-sm font-medium text-gray-600">Starter</div>
            </div>
            <div className="text-center p-4 bg-primary-50 rounded-xl">
              <div className="text-3xl font-bold text-primary-700">
                {stats.subscriptions.artistUsers}
              </div>
              <div className="text-sm font-medium text-primary-600">Artist</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-xl">
              <div className="text-3xl font-bold text-green-700">
                {stats.subscriptions.studioUsers}
              </div>
              <div className="text-sm font-medium text-green-600">Studio</div>
            </div>
          </div>
        </div>
      </div>

      {/* Promocodes */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">
            Promocodes
          </h3>
          <p className="text-sm text-gray-600">Promotional code usage statistics</p>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <div className="text-3xl font-bold text-gray-900">
                {stats.promoCodes.totalCodes}
              </div>
              <div className="text-sm font-medium text-gray-600">Total Codes</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-xl">
              <div className="text-3xl font-bold text-green-700">
                {stats.promoCodes.usedCodes}
              </div>
              <div className="text-sm font-medium text-green-600">Used</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-xl">
              <div className="text-3xl font-bold text-yellow-700">
                {stats.promoCodes.unusedCodes}
              </div>
              <div className="text-sm font-medium text-yellow-600">Unused</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
